import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useCart } from "../contexts/CartContext";
import { Button } from "react-bootstrap";
import { FaStar, FaArrowLeft, FaHeart, FaRegHeart } from "react-icons/fa";
import { API_BASE_URL } from "../config";
import useTitle from "../hooks/useTitle";
import { toast } from "react-toastify";

export default function SingleProductPage() {
  useTitle("Product | Astro Portal");
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addingWishlist, setAddingWishlist] = useState(false);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/product/single`, {
          product_id: slug,
        });
        const productData = res.data.data;
        setProduct(productData);

        // Check if already wishlisted (optional)
        if (productData.is_wishlisted) {
          setIsWishlisted(true);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [slug]);

  const handleAddToCart = async () => {
    setAdding(true);
    try {
      await addToCart(product.id, quantity);
      toast.success("Added to cart!");
    } catch (err) {
      toast.error("Failed to add to cart, item is out of stock.");
    } finally {
      setAdding(false);
    }
  };

  const handleAddToWishlist = async () => {
    const authData = JSON.parse(sessionStorage.getItem("authSession"));

    // 🔒 Check login
    if (!authData || !authData.jwt_token || !authData.user) {
      navigate("/login?redirect=/product/" + product.id);
      return;
    }

    const token = authData.jwt_token;
    const user = authData.user;
    try {
      const response = await axios.post(
        `${API_BASE_URL}/wishlist/add`,
        { product_id: product.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("✅ Wishlist added:", response.data);
      toast.success("Product added to wishlist!");
    } catch (error) {
      console.error("❌ Wishlist add failed:", error);
      if (error.response && error.response.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login?redirect=/product/" + product.id);
      } else {
        toast.error("Something went wrong while adding to wishlist.");
      }
    }
  };

  if (loading) return <p className="text-center mt-5">Loading...</p>;
  if (!product) return <p className="text-center mt-5">Product not found.</p>;

  const media = [
    product.product_image,
    ...(product.media ? product.media.split(",").map((m) => m.trim()) : []),
  ];

  return (
    <div className="container my-5">
      <Button
        className="mb-4 d-flex align-items-center gap-2"
        variant="link"
        onClick={() => navigate(-1)}
      >
        <FaArrowLeft /> Back
      </Button>

      <div className="row g-5">
        {/* LEFT SIDE - IMAGE GALLERY */}
        <div className="col-md-6">
          <div className="border rounded-4 p-3 bg-white shadow-sm position-relative">
            {/* Wishlist Icon */}
            <Button
              variant="light"
              className="position-absolute top-0 end-0 m-3 rounded-circle shadow-sm"
              onClick={handleAddToWishlist}
              disabled={addingWishlist}
              style={{ zIndex: 10 }}
            >
              {isWishlisted ? (
                <FaHeart color="red" size={20} />
              ) : (
                <FaRegHeart size={20} />
              )}
            </Button>

            <div className="text-center mb-3">
              {media[activeImage].endsWith(".mp4") ? (
                <video
                  src={media[activeImage]}
                  controls
                  className="rounded-3 w-100"
                  style={{ maxHeight: "400px", objectFit: "contain" }}
                />
              ) : (
                <img
                  src={media[activeImage]}
                  alt={`product-${activeImage}`}
                  className="img-fluid rounded-3"
                  style={{
                    maxHeight: "400px",
                    objectFit: "contain",
                    width: "100%",
                  }}
                />
              )}
            </div>

            <div className="d-flex justify-content-center gap-2 flex-wrap">
              {media.map((m, i) => (
                <div
                  key={i}
                  className={`border rounded-3 p-1 ${
                    i === activeImage ? "border-primary" : "border-light"
                  }`}
                  style={{
                    width: "70px",
                    height: "70px",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                  onClick={() => setActiveImage(i)}
                >
                  {m.endsWith(".mp4") ? (
                    <video
                      src={m}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : (
                    <img
                      src={m}
                      alt={`thumb-${i}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT SIDE - PRODUCT DETAILS */}
        <div className="col-md-6">
          <div className="border rounded-4 p-4 bg-white shadow-sm">
            <h2 className="fw-bold mb-2">{product.name}</h2>
            <div className="mb-3">
              {product.is_featured && (
                <span className="badge bg-success me-2">Featured</span>
              )}
              {product.is_trending && (
                <span className="badge bg-danger me-2">Trending</span>
              )}
              <span className="badge bg-warning text-dark">
                {product.gst_slab}% GST
              </span>
            </div>

            <p className="text-muted small mb-2">SKU: {product.sku}</p>

            <div className="d-flex align-items-end gap-2 mb-3">
              <h3 className="text-success mb-0">₹{product.sale_price}</h3>
              <span className="text-secondary text-decoration-line-through">
                ₹{product.original_price}
              </span>
            </div>

            <div className="mb-3">
              <strong>Available:</strong>{" "}
              <span
                className={`fw-semibold ${
                  product.quantity > 0 ? "text-success" : "text-danger"
                }`}
              >
                {product.quantity > 0
                  ? `${product.quantity} in stock`
                  : "Out of stock"}
              </span>
            </div>

            <p className="text-secondary mb-3">
              {product.short_description || "No short description available."}
            </p>

            {product.description && (
              <div className="border-top pt-3">
                <h6 className="fw-bold">Product Details</h6>
                <p className="text-secondary">{product.description}</p>
              </div>
            )}

            {/* Quantity & Cart */}
            <div className="d-flex align-items-center gap-3 mt-4">
              <div className="d-flex border rounded">
                <Button
                  variant="light"
                  disabled={quantity <= 1}
                  onClick={() => setQuantity(quantity - 1)}
                >
                  -
                </Button>
                <input
                  type="number"
                  className="form-control text-center"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                  }
                  style={{ width: "60px" }}
                />
                <Button
                  variant="light"
                  onClick={() => setQuantity(quantity + 1)}
                >
                  +
                </Button>
              </div>

              <Button
                variant="primary"
                size="lg"
                disabled={adding || product.quantity === 0}
                onClick={handleAddToCart}
              >
                {adding ? "Adding..." : "Add to Cart"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
